'use client'

import React, { useState } from 'react'
import Script from 'next/script'
import Link from 'next/link'
import { ArrowLeft, CreditCard, Loader2, ExternalLink } from 'lucide-react'

declare global {
  interface Window {
    grecaptcha: {
      ready: (cb: () => void) => void
      execute: (siteKey: string, options: { action: string }) => Promise<string>
    }
  }
}

const RECAPTCHA_SITE_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY!

export default function PayMyBillPage() {
  const [isVerifying, setIsVerifying] = useState(false)
  const [error, setError] = useState('')
  const [scriptLoaded, setScriptLoaded] = useState(false)

  const handlePayClick = async () => {
    setIsVerifying(true)
    setError('')

    try {
      const token = await new Promise<string>((resolve, reject) => {
        window.grecaptcha.ready(() => {
          window.grecaptcha
            .execute(RECAPTCHA_SITE_KEY, { action: 'pay_bill' })
            .then(resolve)
            .catch(reject)
        })
      })

      const res = await fetch('/api/verify-recaptcha', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      })

      const data = await res.json()

      if (!res.ok || !data.success || !data.paymentUrl) {
        setError('Verification failed. Please try again.')
        setIsVerifying(false)
        return
      }

      window.location.href = data.paymentUrl
    } catch {
      setError('Could not verify. Please try again.')
      setIsVerifying(false)
    }
  }

  return (
    <div className="min-h-screen bg-linear-to-b from-slate-50 to-white mt-8">
      <Script
        src={`https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`}
        strategy="afterInteractive"
        onReady={() => setScriptLoaded(true)}
      />

      <div className="max-w-xl mx-auto px-4 py-16 sm:py-24">
        {/* Back link */}
        <Link
          href="/"
          className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-900 mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to homepage
        </Link>

        {/* Header */}
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-brand-blue/10 rounded-lg flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-brand-blue" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900">Pay My Bill</h1>
          </div>
          <p className="text-gray-600">
            Make a secure payment to Serotte Law Firm. Click the button below to proceed to our
            secure payment portal powered by Stripe.
          </p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6 sm:p-8 space-y-6">
          <button
            type="button"
            onClick={handlePayClick}
            disabled={!scriptLoaded || isVerifying}
            className="w-full py-3.5 bg-brand-blue text-white font-semibold rounded-lg hover:bg-brand-blue/90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isVerifying ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Verifying...
              </>
            ) : (
              <>
                <ExternalLink className="w-5 h-5" />
                Pay My Bill
              </>
            )}
          </button>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
